// API service for backend communication

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ADMIN_TOKEN_KEY = 'admin_token';
const ADMIN_USER_KEY = 'admin_user';

const getAuthToken = () => localStorage.getItem('auth_token');
export const getAdminToken = () => sessionStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminSession = (token, user) => {
  if (token) sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  if (user) sessionStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
};
export const clearAdminSession = () => {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_USER_KEY);
};
export const getAdminUser = () => {
  try {
    const raw = sessionStorage.getItem(ADMIN_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const handleResponse = async (response) => {
  const text = await response.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { error: text || 'API request failed' };
  }
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  return data;
};

const adminHeaders = (extra = {}) => {
  const headers = { ...extra };
  const token = getAdminToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const adminFetch = (path, options = {}) => {
  const headers = adminHeaders(options.headers || {});
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  }).then(handleResponse);
};

// Applicant (careers portal) auth
export const authAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await handleResponse(response);
    if (data.token) {
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  register: async (username, email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password }),
    });
    return handleResponse(response);
  },

  getCurrentUser: async () => {
    const token = getAuthToken();
    if (!token) throw new Error('No authentication token found');
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return handleResponse(response);
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  },
};

// Admin dashboard auth (server-validated)
export const adminAuthAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await handleResponse(response);
    if (data.token) setAdminSession(data.token, data.user);
    return data;
  },

  me: async () => {
    const data = await adminFetch('/auth/admin/me');
    if (data.user) setAdminSession(getAdminToken(), data.user);
    return data;
  },

  logout: async () => {
    try {
      await adminFetch('/auth/admin/logout', { method: 'POST' });
    } catch (_) {
      /* ignore */
    }
    clearAdminSession();
    // Clear legacy client-only flag if present
    localStorage.removeItem('admin_auth');
  },
};

export const equipmentAPI = {
  getAll: async () => handleResponse(await fetch(`${API_BASE_URL}/equipment`)),
  getBySlug: async (slug) => handleResponse(await fetch(`${API_BASE_URL}/equipment/${slug}`)),
  create: async (equipmentData) =>
    adminFetch('/equipment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(equipmentData),
    }),
  update: async (id, equipmentData) =>
    adminFetch(`/equipment/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(equipmentData),
    }),
  delete: async (id) => adminFetch(`/equipment/${id}`, { method: 'DELETE' }),
};

export const workersAPI = {
  getAll: () => adminFetch('/workers'),
  create: (workerData) =>
    adminFetch('/workers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(workerData),
    }),
  update: (id, workerData) =>
    adminFetch(`/workers/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(workerData),
    }),
  delete: (id) => adminFetch(`/workers/${id}`, { method: 'DELETE' }),
};

const workerFetch = (path, options = {}) =>
  fetch(`${API_BASE_URL}/workers${path}`, { ...options, credentials: 'include' }).then(handleResponse);
const jsonBody = (method, data) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data || {}) });

// Worker portal: httpOnly cookie session (go_worker_token), like admin.
export const workerPortalAPI = {
  login: (email, password) => workerFetch('/login', jsonBody('POST', { email, password })),
  logout: () => workerFetch('/logout', { method: 'POST' }),
  me: () => workerFetch('/me'),
  updateMe: (data) => workerFetch('/me', jsonBody('PUT', data)),
  track: (data) => workerFetch('/track', jsonBody('POST', data)),
};

const makeAdminAPI = (path) => ({
  getAll: () => adminFetch(`/${path}`),
  create: (data) =>
    adminFetch(`/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    adminFetch(`/${path}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  delete: (id) => adminFetch(`/${path}/${id}`, { method: 'DELETE' }),
});

export const projectsAPI = makeAdminAPI('projects');
export const salesAPI = makeAdminAPI('sales');
export const shipmentsAPI = makeAdminAPI('shipments');
export const fleetAPI = makeAdminAPI('fleet');

export const trainingAPI = {
  // Catalog is public (worker portals); mutations below require admin
  getAll: () => fetch(`${API_BASE_URL}/training`).then(handleResponse),
  create: (data) =>
    adminFetch('/training', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (id, data) =>
    adminFetch(`/training/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  delete: (id) => adminFetch(`/training/${id}`, { method: 'DELETE' }),
  getAssignments: () => adminFetch('/training/assignments'),
  assign: (courseId, workerIds) =>
    adminFetch('/training/assignments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ courseId, workerIds }),
    }),
  unassign: (id) => adminFetch(`/training/assignments/${id}`, { method: 'DELETE' }),
};

export const courseProgressAPI = {
  getByWorker: (workerId) => fetch(`${API_BASE_URL}/course-progress/${workerId}`).then(handleResponse),
  getAll: () => adminFetch('/course-progress/all'),
  save: (workerId, courseId, data) =>
    fetch(`${API_BASE_URL}/course-progress/${workerId}/${courseId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),
};

export const careersAPI = {
  getJobs: () => fetch(`${API_BASE_URL}/careers/jobs`).then(handleResponse),

  apply: ({ job_id, cover_letter, resume }) => {
    const formData = new FormData();
    if (job_id) formData.append('job_id', job_id);
    if (cover_letter) formData.append('cover_letter', cover_letter);
    if (resume) formData.append('resume', resume);
    return fetch(`${API_BASE_URL}/careers/apply`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
      body: formData,
    }).then(handleResponse);
  },

  getMyApplications: () =>
    fetch(`${API_BASE_URL}/careers/applications/me`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    }).then(handleResponse),

  getAllJobs: () => adminFetch('/careers/jobs/all'),
  createJob: (data) =>
    adminFetch('/careers/jobs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  updateJob: (id, data) =>
    adminFetch(`/careers/jobs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  deleteJob: (id) => adminFetch(`/careers/jobs/${id}`, { method: 'DELETE' }),
  getAllApplications: () => adminFetch('/careers/applications'),
  getApplication: (id) => adminFetch(`/careers/applications/${id}`),
  updateApplicationStatus: (id, status, notes) =>
    adminFetch(`/careers/applications/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, notes }),
    }),
  resumeUrl: (id) => `${API_BASE_URL}/careers/applications/${id}/resume`,
  downloadResume: async (id, filename) => {
    const token = getAdminToken();
    const res = await fetch(`${API_BASE_URL}/careers/applications/${id}/resume`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) {
      let msg = 'Resume download failed';
      try {
        const j = await res.json();
        msg = j.error || msg;
      } catch (_) {}
      throw new Error(msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'resume';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

const MAIL_PROXY = (process.env.REACT_APP_MAIL_PROXY_URL || '').replace(/\/$/, '') || '';
const mailUrl = (path) => (MAIL_PROXY ? `${MAIL_PROXY}${path}` : path);

export const mailAPI = {
  status: () => fetch(mailUrl('/api/mail/status')).then(handleResponse),
  send: (payload) =>
    fetch(mailUrl('/api/mail/send'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(handleResponse),
};

export const isAuthenticated = () => !!getAuthToken();
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};
